import { useCallback, useEffect, useMemo, useRef } from "react";

/* 알림톡 버튼 진입 전용 랜딩 페이지.
 *
 * 알림톡은 웹링크(WL) 버튼에 OS 구분 없이 URL 하나만 등록할 수 있으므로,
 * 이 페이지에서 기기를 판별해 앱 실행 또는 스토어 이동을 처리한다.
 *
 * 진입 직후(자동): 커스텀 스킴으로 앱 실행을 시도하고 3초간 지켜본다.
 *   설치됨   → 앱으로 전환되고, 화면이 가려지는 시점에 스토어 이동 예약을 취소한다.
 *   미설치   → 3초 뒤 스토어로 이동한다.
 *
 * AOS 에서 intent:// 는 자동·수동 어디에도 쓰지 않는다.
 *   - fallback 이 없으면 미설치 시 즉시 플레이스토어로 튕겨 대기 시간이 무의미해진다.
 *   - fallback 을 이 페이지로 주면 재로딩되어 화면이 깜빡인다.
 *   - 외부 앱 실행 요청으로 처리되어 카카오톡 인앱 브라우저가 닫힌다.
 * 대신 커스텀 스킴을 시도하고, 전환되지 않으면 일반 https 스토어 링크로 이동한다.
 * 이렇게 하면 스토어에서 뒤로 나왔을 때 인앱 브라우저가 그대로 남는다.
 *
 * 버튼(수동): 설치됨 → 앱 실행 / 미설치 → 스토어 이동.
 *   AOS 는 intent:// 의 browser_fallback_url 로 분기가 한 번에 처리되고,
 *   iOS 는 스킴 실행 후 화면이 가려지지 않으면 미설치로 보고 앱스토어로 보낸다.
 *
 * 쿼리
 *   url  : 앱에서 열 화면 경로 (예: /setting/tournament-history/123/456)
 *   utm_*: 스토어 이동 경로에만 전달
 *
 * 주의: 기존 /app-open 은 앱 설치 유도(마케팅) 용도로 계속 쓰이므로 건드리지 않는다.
 * 상수는 그쪽과 중복되는데, 한쪽 변경이 다른 쪽에 번지지 않도록 의도적으로 분리해 둔다.
 */

type Platform = "ios" | "android" | "pc";

const APP_CUSTOM_SCHEME = "voicepark-app";
const APP_WEB_HOST = "www.vcparkgolf.com";
const APP_AOS_STORE =
  "https://play.google.com/store/apps/details?id=com.fixelsoft.voicepark";
const APP_IOS_STORE = "https://apps.apple.com/app/id6749922967";

// 진입 직후 앱 전환 여부를 지켜보는 시간.
const APP_DETECT_TIMEOUT_MS = 3000;
// 버튼으로 앱 실행을 시도한 뒤 스토어로 보낼지 판단하는 시간.
const MANUAL_OPEN_TIMEOUT_MS = 1000;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
] as const;

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  if (/iP(hone|od|ad)/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "pc";
}

// 앱에서 열 경로. 외부 입력이므로 같은 출처 경로만 허용한다.
function readTargetPath(): string {
  const raw = new URLSearchParams(window.location.search).get("url");
  if (!raw) return "/";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
}

function readUtmQuery(): string {
  const params = new URLSearchParams(window.location.search);
  return UTM_KEYS.filter((key) => params.get(key))
    .map((key) => `${key}=${params.get(key)}`)
    .join("&");
}

function buildAndroidStoreUrl(utmQuery: string): string {
  if (!utmQuery) return APP_AOS_STORE;
  return `${APP_AOS_STORE}&referrer=${encodeURIComponent(utmQuery)}`;
}

function buildAppSchemeUrl(targetPath: string): string {
  return `${APP_CUSTOM_SCHEME}://${APP_WEB_HOST}${targetPath}`;
}

/* 앱 실행 시도. 커스텀 스킴만 동작한다.
 * 유니버설 링크는 카카오톡 웹뷰가 가로채지 않아 앱이 실행되지 않는다.
 * iOS 는 앱 전환 후 카카오톡으로 돌아오면 인앱 브라우저가 닫히는데,
 * iframe / window.open 으로 바꿔도 동일해 카카오톡 웹뷰 자체 동작으로 확인했다.
 */
function requestAppOpen(targetPath: string) {
  window.location.href = buildAppSchemeUrl(targetPath);
}

export default function AppLink() {
  const platform = useMemo(detectPlatform, []);
  const targetPath = useMemo(readTargetPath, []);
  const utmQuery = useMemo(readUtmQuery, []);

  const timersRef = useRef<number[]>([]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  // 자동 진입: 커스텀 스킴으로 앱 실행을 시도하고, 3초 안에 전환되지 않으면 스토어로 보낸다.
  useEffect(() => {
    if (platform === "pc") return;

    requestAppOpen(targetPath);

    const timer = window.setTimeout(() => {
      if (document.visibilityState !== "visible") return;
      window.location.href =
        platform === "ios" ? APP_IOS_STORE : buildAndroidStoreUrl(utmQuery);
    }, APP_DETECT_TIMEOUT_MS);
    timersRef.current.push(timer);

    return clearTimers;
  }, [platform, targetPath, utmQuery, clearTimers]);

  // 앱으로 전환되면 남아 있는 예약 동작을 모두 취소한다.
  useEffect(() => {
    const cancelOnHide = () => {
      if (document.visibilityState === "hidden") clearTimers();
    };
    document.addEventListener("visibilitychange", cancelOnHide);
    return () => document.removeEventListener("visibilitychange", cancelOnHide);
  }, [clearTimers]);

  // 수동 이동: 설치되어 있으면 앱, 아니면 스토어.
  // AOS 도 intent:// 를 쓰지 않는다. 외부 앱 실행 요청으로 처리되어 카카오톡 인앱
  // 브라우저가 닫히기 때문이다. 일반 https 스토어 링크로 가면 브라우저가 남는다.
  const goToApp = useCallback(() => {
    if (platform === "pc") return;

    requestAppOpen(targetPath);

    const timer = window.setTimeout(() => {
      if (document.visibilityState !== "visible") return;
      window.location.href =
        platform === "ios" ? APP_IOS_STORE : buildAndroidStoreUrl(utmQuery);
    }, MANUAL_OPEN_TIMEOUT_MS);
    timersRef.current.push(timer);
  }, [platform, targetPath, utmQuery]);

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-[20px] bg-white px-[20px] text-center">
      <img
        src="/images/img-login-logo.webp"
        alt="보이스파크"
        className="w-[80%] object-contain"
      />
      <p className="text-base font-semibold text-[#222] break-keep">
        보이스파크 APP에서
        <br />
        다양한 행사와 혜택을 만나보세요
      </p>

      <button
        type="button"
        onClick={goToApp}
        className="w-full max-w-[320px] rounded-[10px] bg-primary py-[12px] text-base font-medium text-white"
      >
        보이스파크 APP으로 이동하기
      </button>
    </div>
  );
}
